import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { mintSocketTicket } from '../api/client';
import { classifyFailure } from '../lib/offline/retry-policy';
import {
  useChatColor,
  CHAT_TOKENS as T,
  CHAT_ACCENT_FILL,
  CHAT_ACCENT_TEXT,
  CHAT_ACCENT_SIGNAL,
} from '../hooks/useChatPalette';

interface ChatNotification {
  id: string;
  jobId: string;
  jobNumber: string;
  senderName: string;
  senderInitials: string;
  messagePreview: string;
  createdAt: string;
}

/**
 * Reconnect backoff for the notification socket.
 *
 * 1s, 2s, 4s, 8s, 16s, then 30s forever. The two ends are picked from the two
 * things that actually close this socket:
 *
 * - The fast end is a wifi handoff or a tunnel — seconds. Starting at 1s means
 *   the common case is invisible to the user, which matters because there is no
 *   polling fallback behind this socket the way there is behind `useJobChat`;
 *   every second disconnected is a notification nobody gets.
 * - The slow end is a Railway deploy or a laptop that slept. Those are tens of
 *   seconds to hours, and a 30s ceiling costs at most 2 mint requests per minute
 *   per open tab while it lasts. The ceiling is the point: the retry never stops
 *   for a transient failure, so what has to be bounded is the RATE, not the
 *   count.
 *
 * No jitter, deliberately. Jitter's job is to stop a herd re-connecting in
 * lockstep after a server restart, and the herd here is the privileged users of
 * one tenant with the app open — tens, not thousands. Jitter would also make the
 * schedule untestable, and an unverified backoff is how you end up with the
 * retry storm in PR #293. Add jitter when this socket is opened by field roles
 * too, or when a deploy visibly spikes `/realtime/socket-ticket`.
 */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * How long a socket has to survive before the next drop is treated as a fresh
 * outage rather than a continuing one.
 *
 * Resetting the backoff on `onopen` alone is the obvious rule and it is wrong:
 * a server that completes the handshake and then immediately closes — a ticket
 * the WS layer will not accept, a backend shedding load — would reset the
 * counter on every attempt and hammer at 1s indefinitely. That is the same
 * shape of bug as PR #293's, where a failure that could never succeed was
 * retried forever. So the counter resets only after a connection that lasted
 * long enough to have been a real one.
 */
const STABLE_CONNECTION_MS = 30_000;

/** The socket endpoint. Built per attempt because the ticket changes per attempt. */
function buildSocketUrl(ticket: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const wsHost = apiUrl.startsWith('http')
    ? apiUrl.replace(/^https?:\/\//, '').replace(/\/api\/v1$/, '')
    : window.location.host;

  return `${protocol}//${wsHost}/ws?ticket=${encodeURIComponent(ticket)}`;
}

/**
 * Global chat notification toast provider.
 * Connects to WebSocket and shows pop-up alerts when crew sends chat messages.
 * Only active for admin/dispatcher/office_staff/supervisor roles.
 */
export default function ChatNotificationToast() {
  const { user, isAuthenticated } = useAuth();
  const [toasts, setToasts] = useState<ChatNotification[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const navigate = useNavigate();
  const c = useChatColor();

  const privilegedRoles = ['admin', 'dispatcher', 'office_staff', 'supervisor'];
  const isPrivileged = isAuthenticated && user?.role && privilegedRoles.includes(user.role);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Connect WebSocket for privileged users, and RECONNECT when it drops.
  //
  // Authenticated with a 60-second ticket from `mintSocketTicket()`, not the
  // session JWT — see that helper for why a query-string credential has to be
  // short-lived. This socket is the wider half of that exposure: PR #319 revived
  // this component (it had been reading a key nothing writes, so it never
  // connected), and it holds an open socket for every admin, dispatcher,
  // office_staff and supervisor for their whole session — not just the users who
  // happen to have a chat drawer open.
  //
  // WHY THE RECONNECT IS NOT A `setTimeout(connect, 5000)`
  //
  // A ticket is time-boxed at 60 seconds, so the one this socket opened with is
  // dead long before most retries fire. Every attempt therefore mints a FRESH
  // one; nothing here caches or reuses a ticket, which is also what makes each
  // attempt re-ask the question "is this session still valid?".
  //
  // That re-ask is the whole failure triage, and it lives at the mint rather
  // than at the socket. A closed socket tells you almost nothing — 1006 is
  // "abnormal" for a dead wifi radio and for a rebooting server alike — so every
  // close is treated as transient and the NEXT mint decides whether to continue:
  //
  //   rejected (401/403)   the session is gone or the user was deactivated.
  //                        Retrying cannot fix it, so it stops. Permanently, for
  //                        this effect run. Without this a logged-out tab storms
  //                        /realtime/socket-ticket once per attempt forever,
  //                        which is PR #293's bug wearing a different hat.
  //   server   (5xx/408/429) the server failed, not the credential. Deploys and
  //                        rate limits pass. Keeps retrying.
  //   network  (no response) nothing was ever judged — offline, tunnel, sleep.
  //                        Keeps retrying, and per `retry-policy` never exhausts.
  //
  // Classification is `classifyFailure` from the offline retry policy rather
  // than a second axios-status reader written here: it is the same question
  // about the same error shape, and PR #293 is precisely the story of what
  // happens when two retry loops each decide it for themselves.
  //
  // Only `onclose` schedules. A browser fires `onerror` immediately followed by
  // `onclose` on a failed connection, so scheduling from both would halve every
  // backoff interval.
  //
  // The mint is async, so the socket is created after an await and the cleanup
  // has to cope with unmounting while that promise is still in flight — hence
  // `cancelled`, checked again after every await and before every timer body.
  useEffect(() => {
    if (!isPrivileged) return;

    let ws: WebSocket | null = null;
    let cancelled = false;
    let stopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const scheduleReconnect = () => {
      // `stopped` is the auth verdict; `cancelled` is unmount. Either one means
      // no timer may be armed — this is the check that keeps a component
      // unmounting mid-retry from leaving one behind.
      if (cancelled || stopped || retryTimer) return;

      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      attempt += 1;

      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (cancelled || stopped) return;
        connect();
      }, delay);
    };

    const connect = () => {
      mintSocketTicket().then(ticket => {
        if (cancelled || stopped) return;

        // Do not log this URL.
        const socket = new WebSocket(buildSocketUrl(ticket));
        ws = socket;
        wsRef.current = socket;

        // Captured per socket, not per effect: by the time a handler fires, the
        // outer `ws` may already point at a newer attempt.
        let openedAt = 0;

        socket.onopen = () => {
          openedAt = Date.now();
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'job_chat_notification') {
              const notif: ChatNotification = {
                id: `${data.data.jobId}-${Date.now()}`,
                ...data.data,
              };
              setToasts(prev => [...prev, notif]);

              // Auto-dismiss after 6 seconds
              setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== notif.id));
              }, 6000);
            }
          } catch {}
        };

        socket.onclose = () => {
          if (wsRef.current === socket) wsRef.current = null;

          // A connection that lasted is evidence the last outage is over, so the
          // next one starts its backoff from scratch. A connection that opened
          // and died immediately is not — see STABLE_CONNECTION_MS.
          if (openedAt > 0 && Date.now() - openedAt >= STABLE_CONNECTION_MS) {
            attempt = 0;
          }

          scheduleReconnect();
        };
      }).catch(err => {
        if (cancelled) return;

        if (classifyFailure(err) === 'rejected') {
          // 401/403 and friends: no number of retries produces a valid session.
          // The toast is absent until this component remounts with a real one.
          stopped = true;
          return;
        }

        scheduleReconnect();
      });
    };

    connect();

    return () => {
      // Order matters: `cancelled` first, because `close()` synchronously fires
      // `onclose` in some environments and that handler schedules.
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      ws?.close();
      ws = null;
      wsRef.current = null;
    };
  }, [isPrivileged]);

  if (!isPrivileged || toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '16px',
      right: '16px',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      maxWidth: '380px',
      width: '100%',
    }}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          onClick={() => {
            dismissToast(toast.id);
            navigate(`/jobs/${toast.jobId}`);
          }}
          style={{
            backgroundColor: 'hsl(var(--secondary))',
            border: '1px solid hsl(var(--border))',
            /* The left rule is an indicator rather than a fill, which is the
               distinction `--fieldiq-accent-signal` exists to carry: the same
               gold in dark, amber-700 in light, where the gold sits at 1.74:1
               on this surface. */
            borderLeft: `4px solid ${CHAT_ACCENT_SIGNAL}`,
            borderRadius: '12px',
            padding: '12px 14px',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            animation: 'slideInRight 0.3s ease-out',
          }}
        >
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Sender avatar */}
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: CHAT_ACCENT_FILL,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 600,
                color: CHAT_ACCENT_TEXT,
                flexShrink: 0,
              }}>
                {toast.senderInitials || toast.senderName.substring(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: c('#fff', T.heading) }}>
                  {toast.senderName}
                </div>
                <div style={{ fontSize: '11px', color: c('#94a3b8', T.muted) }}>
                  Job {toast.jobNumber}
                </div>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissToast(toast.id);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: c('#64748b', T.muted),
                fontSize: '16px',
                cursor: 'pointer',
                padding: '2px 4px',
                lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>
          {/* Message preview */}
          <div style={{ fontSize: '13px', color: c('#cbd5e1', T.text), lineHeight: 1.4 }}>
            {toast.messagePreview}
          </div>
        </div>
      ))}

      {/* Inline keyframes */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
